import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthenticationContext } from "../../controls/auth/AuthenticationWrap";
import { UserRole } from "../../../models/User";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import {
	BreadcrumbItem,
	BreadcrumbSeparator,
	DebtBreadcrumb,
	DebtContainer,
	DebtTitle,
} from "../DebtManagement/styled/DebtManagementStyles";
import {
	AdminConsentLogRecord,
	ConsentType,
	GetAdminConsentLogsParams,
	getAdminConsentLogs,
	buildExportUrl,
} from "../../../services/ConsentLogService";
import exportToExcel from "../MyBooking/hook/exportToExcel";

type TabType = "station" | "customer";

type StationFilters = {
	partnerId: string;
	documentKey: string;
	fromDate: string;
	toDate: string;
};

type CustomerFilters = {
	orderId: string;
	email: string;
	fromDate: string;
	toDate: string;
};

const STATION_DOC_OPTIONS = [
	{ value: "", label: "All documents" },
	{ value: "station-doc-1", label: "Thỏa thuận hợp tác kinh doanh" },
	{ value: "station-doc-2", label: "Phụ lục Station độc quyền" },
	{ value: "station-doc-3", label: "Chính sách hợp tác WhaleLO Station" },
	{ value: "station-doc-4", label: "Operation Guide (Bản đầy đủ)" },
	{ value: "station-doc-5", label: "Quick Guide" },
];

const CONSENT_TYPE_LABELS: Record<string, string> = {
	station_agreement: "Agreement",
	station_view_only: "View Only",
	customer_tc: "T&C",
};

function formatDateTimeVn(value: string | null | undefined) {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("vi-VN", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(date);
}

function getPartnerLabel(log: AdminConsentLogRecord) {
	const name = log.partner?.displayName || log.partner?.name;
	if (name) return `${name} (#${log.partnerId})`;
	if (log.partnerId) return `#${log.partnerId}`;
	return "-";
}

export default function ConsentLogsPage() {
	const authContext = useContext(AuthenticationContext);
	const { userInfo, isFinishedValidate } = authContext || {};
	const isDenied = isFinishedValidate && (!userInfo || userInfo.role !== UserRole.Admin);

	const [activeTab, setActiveTab] = useState<TabType>("station");
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [logs, setLogs] = useState<AdminConsentLogRecord[]>([]);
	const [page, setPage] = useState(1);
	const [limit] = useState(20);
	const [total, setTotal] = useState(0);

	const [stationFilters, setStationFilters] = useState<StationFilters>({ partnerId: "", documentKey: "", fromDate: "", toDate: "" });
	const [appliedStationFilters, setAppliedStationFilters] = useState<StationFilters>({ partnerId: "", documentKey: "", fromDate: "", toDate: "" });

	const [customerFilters, setCustomerFilters] = useState<CustomerFilters>({ orderId: "", email: "", fromDate: "", toDate: "" });
	const [appliedCustomerFilters, setAppliedCustomerFilters] = useState<CustomerFilters>({ orderId: "", email: "", fromDate: "", toDate: "" });

	const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

	const buildParams = useCallback((): GetAdminConsentLogsParams => {
		if (activeTab === "station") {
			return {
				consentType: appliedStationFilters.documentKey
					? undefined
					: ("station_agreement" as ConsentType),
				partnerId: appliedStationFilters.partnerId || undefined,
				documentKey: appliedStationFilters.documentKey || undefined,
				fromDate: appliedStationFilters.fromDate || undefined,
				toDate: appliedStationFilters.toDate || undefined,
				page,
				limit,
			};
		}
		return {
			consentType: "customer_tc" as ConsentType,
			orderId: appliedCustomerFilters.orderId || undefined,
			fromDate: appliedCustomerFilters.fromDate || undefined,
			toDate: appliedCustomerFilters.toDate || undefined,
			page,
			limit,
		};
	}, [activeTab, appliedStationFilters, appliedCustomerFilters, page, limit]);

	const load = useCallback(async () => {
		if (isDenied) return;
		setLoading(true);
		try {
			const params = buildParams();
			const resp = await getAdminConsentLogs(params);
			setLogs(resp.data || []);
			setTotal(resp.total || 0);
		} catch (error: any) {
			alert(`Có lỗi xảy ra: ${error?.response?.data?.message || error.message || "Vui lòng thử lại"}`);
		} finally {
			setLoading(false);
		}
	}, [buildParams, isDenied]);

	useEffect(() => {
		load();
	}, [load]);

	const onTabChange = (tab: TabType) => {
		setActiveTab(tab);
		setPage(1);
		setLogs([]);
	};

	const onApplyStationFilters = () => {
		setPage(1);
		setAppliedStationFilters(stationFilters);
	};

	const onResetStationFilters = () => {
		const next = { partnerId: "", documentKey: "", fromDate: "", toDate: "" };
		setStationFilters(next);
		setAppliedStationFilters(next);
		setPage(1);
	};

	const onApplyCustomerFilters = () => {
		setPage(1);
		setAppliedCustomerFilters(customerFilters);
	};

	const onResetCustomerFilters = () => {
		const next = { orderId: "", email: "", fromDate: "", toDate: "" };
		setCustomerFilters(next);
		setAppliedCustomerFilters(next);
		setPage(1);
	};

	const onExport = async () => {
		setExporting(true);
		try {
			const params = buildParams();
			const resp = await getAdminConsentLogs({ ...params, limit: 10000, page: 1 });
			const rows = resp.data;

			if (activeTab === "station") {
				exportToExcel(
					rows.map((log) => ({
						"Time (VN)": formatDateTimeVn(log.agreedAt),
						"Station Name": getPartnerLabel(log),
						"Document": STATION_DOC_OPTIONS.find((o) => o.value === log.documentKey)?.label || log.documentKey,
						"Consent Type": CONSENT_TYPE_LABELS[log.consentType] || log.consentType,
						"Version": log.documentVersion,
						"IP Address": log.ipAddress || "",
					})),
					"station-consent-logs.xlsx",
				);
			} else {
				exportToExcel(
					rows.map((log) => ({
						"Time (VN)": formatDateTimeVn(log.agreedAt),
						"Booking ID": log.orderId || "",
						"T&C Version": log.documentVersion,
						"Email": log.email || "",
						"IP Address": log.ipAddress || "",
					})),
					"customer-consent-logs.xlsx",
				);
			}
		} catch (error: any) {
			alert(`Export thất bại: ${error?.response?.data?.message || error.message || "Vui lòng thử lại"}`);
		} finally {
			setExporting(false);
		}
	};

	if (isDenied) {
		return (
			<MyBookingWrapper>
				<OrderInner>
					<div style={{ textAlign: "center", padding: "50px" }}>
						<h2>Access Denied</h2>
						<p>You don't have permission to access this page.</p>
					</div>
				</OrderInner>
			</MyBookingWrapper>
		);
	}

	return (
		<DebtContainer>
			<DebtBreadcrumb>
				<BreadcrumbItem>Admin</BreadcrumbItem>
				<BreadcrumbSeparator src="./static/img/direction-right.png" alt=">" />
				<BreadcrumbItem>Consent Logs</BreadcrumbItem>
			</DebtBreadcrumb>

			<DebtTitle>Consent Logs</DebtTitle>

			{/* Tabs */}
			<div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
				<button
					onClick={() => onTabChange("station")}
					style={activeTab === "station" ? activeTabStyle : tabStyle}
				>
					Station Consent Logs
				</button>
				<button
					onClick={() => onTabChange("customer")}
					style={activeTab === "customer" ? activeTabStyle : tabStyle}
				>
					Customer Consent Logs
				</button>
			</div>

			{/* Filters */}
			<div style={{ background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 8px 24px rgba(15,23,42,0.08)", marginBottom: 16 }}>
				{activeTab === "station" ? (
					<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "end" }}>
						<div>
							<div style={labelStyle}>Station ID</div>
							<input
								value={stationFilters.partnerId}
								onChange={(e) => setStationFilters((p) => ({ ...p, partnerId: e.target.value }))}
								placeholder="VD: 208"
								style={inputStyle}
							/>
						</div>
						<div>
							<div style={labelStyle}>Document</div>
							<select
								value={stationFilters.documentKey}
								onChange={(e) => setStationFilters((p) => ({ ...p, documentKey: e.target.value }))}
								style={inputStyle}
							>
								{STATION_DOC_OPTIONS.map((o) => (
									<option key={o.value} value={o.value}>{o.label}</option>
								))}
							</select>
						</div>
						<div>
							<div style={labelStyle}>From Date</div>
							<input type="date" value={stationFilters.fromDate} onChange={(e) => setStationFilters((p) => ({ ...p, fromDate: e.target.value }))} style={inputStyle} />
						</div>
						<div>
							<div style={labelStyle}>To Date</div>
							<input type="date" value={stationFilters.toDate} onChange={(e) => setStationFilters((p) => ({ ...p, toDate: e.target.value }))} style={inputStyle} />
						</div>
						<div style={{ display: "flex", gap: 8 }}>
							<button onClick={onApplyStationFilters} style={primaryButtonStyle}>Apply</button>
							<button onClick={onResetStationFilters} style={secondaryButtonStyle}>Reset</button>
						</div>
					</div>
				) : (
					<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "end" }}>
						<div>
							<div style={labelStyle}>Booking ID</div>
							<input
								value={customerFilters.orderId}
								onChange={(e) => setCustomerFilters((p) => ({ ...p, orderId: e.target.value }))}
								placeholder="VD: 1234"
								style={inputStyle}
							/>
						</div>
						<div>
							<div style={labelStyle}>Email</div>
							<input
								value={customerFilters.email}
								onChange={(e) => setCustomerFilters((p) => ({ ...p, email: e.target.value }))}
								placeholder="customer@email.com"
								style={inputStyle}
							/>
						</div>
						<div>
							<div style={labelStyle}>From Date</div>
							<input type="date" value={customerFilters.fromDate} onChange={(e) => setCustomerFilters((p) => ({ ...p, fromDate: e.target.value }))} style={inputStyle} />
						</div>
						<div>
							<div style={labelStyle}>To Date</div>
							<input type="date" value={customerFilters.toDate} onChange={(e) => setCustomerFilters((p) => ({ ...p, toDate: e.target.value }))} style={inputStyle} />
						</div>
						<div style={{ display: "flex", gap: 8 }}>
							<button onClick={onApplyCustomerFilters} style={primaryButtonStyle}>Apply</button>
							<button onClick={onResetCustomerFilters} style={secondaryButtonStyle}>Reset</button>
						</div>
					</div>
				)}
			</div>

			{/* Table */}
			<div style={{ background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
					<div style={{ fontSize: 14, color: "#475569" }}>
						Showing {logs.length} / {total} records
					</div>
					<button onClick={onExport} disabled={exporting || loading} style={primaryButtonStyle}>
						{exporting ? "Exporting..." : "Export Excel"}
					</button>
				</div>

				<div style={{ overflowX: "auto" }}>
					{activeTab === "station" ? (
						<table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
							<thead>
								<tr style={{ background: "#f8fafc" }}>
									{["Time (VN)", "Station Name", "Document", "Type", "Version", "IP Address"].map((h) => (
										<th key={h} style={tableHeadStyle}>{h}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr><td colSpan={6} style={emptyCellStyle}>Loading...</td></tr>
								) : logs.length === 0 ? (
									<tr><td colSpan={6} style={emptyCellStyle}>No consent logs found.</td></tr>
								) : logs.map((log) => (
									<tr key={log.id} style={{ borderTop: "1px solid #e2e8f0" }}>
										<td style={tableCellStyle}>{formatDateTimeVn(log.agreedAt)}</td>
										<td style={tableCellStyle}>{getPartnerLabel(log)}</td>
										<td style={tableCellStyle}>{STATION_DOC_OPTIONS.find((o) => o.value === log.documentKey)?.label || log.documentKey}</td>
										<td style={tableCellStyle}>{CONSENT_TYPE_LABELS[log.consentType] || log.consentType}</td>
										<td style={tableCellStyle}>{log.documentVersion}</td>
										<td style={tableCellStyle}>{log.ipAddress || "-"}</td>
									</tr>
								))}
							</tbody>
						</table>
					) : (
						<table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
							<thead>
								<tr style={{ background: "#f8fafc" }}>
									{["Time (VN)", "Booking ID", "T&C Version", "Email", "IP Address"].map((h) => (
										<th key={h} style={tableHeadStyle}>{h}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr><td colSpan={5} style={emptyCellStyle}>Loading...</td></tr>
								) : logs.length === 0 ? (
									<tr><td colSpan={5} style={emptyCellStyle}>No consent logs found.</td></tr>
								) : logs.map((log) => (
									<tr key={log.id} style={{ borderTop: "1px solid #e2e8f0" }}>
										<td style={tableCellStyle}>{formatDateTimeVn(log.agreedAt)}</td>
										<td style={tableCellStyle}>{log.orderId || "-"}</td>
										<td style={tableCellStyle}>{log.documentVersion}</td>
										<td style={tableCellStyle}>{log.email || "-"}</td>
										<td style={tableCellStyle}>{log.ipAddress || "-"}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>

				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, gap: 12, flexWrap: "wrap" }}>
					<div style={{ fontSize: 14, color: "#475569" }}>Page {page} / {totalPages}</div>
					<div style={{ display: "flex", gap: 8 }}>
						<button style={secondaryButtonStyle} onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1 || loading}>Prev</button>
						<button style={secondaryButtonStyle} onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={page >= totalPages || loading}>Next</button>
					</div>
				</div>
			</div>
		</DebtContainer>
	);
}

const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, marginBottom: 6 };

const inputStyle: React.CSSProperties = {
	width: "100%",
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 12px",
	fontSize: 14,
	background: "#fff",
};

const primaryButtonStyle: React.CSSProperties = {
	border: "none",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#0f62fe",
	color: "#fff",
	fontWeight: 600,
	cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#fff",
	color: "#0f172a",
	fontWeight: 600,
	cursor: "pointer",
};

const tabStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 20px",
	background: "#fff",
	color: "#475569",
	fontWeight: 600,
	cursor: "pointer",
	fontSize: 14,
};

const activeTabStyle: React.CSSProperties = {
	...tabStyle,
	background: "#0f62fe",
	color: "#fff",
	border: "none",
};

const tableHeadStyle: React.CSSProperties = {
	padding: "12px 10px",
	textAlign: "left",
	fontSize: 13,
	fontWeight: 700,
	color: "#334155",
	borderBottom: "1px solid #e2e8f0",
	whiteSpace: "nowrap",
};

const tableCellStyle: React.CSSProperties = {
	padding: "12px 10px",
	fontSize: 14,
	color: "#0f172a",
	verticalAlign: "top",
};

const emptyCellStyle: React.CSSProperties = {
	padding: "32px 12px",
	textAlign: "center",
	color: "#64748b",
	fontSize: 14,
};
